import { TransmissionMessage } from '../../../reducers/messagingSlice';

/**
 * AI-suggestion consumption (AI -> HITL, the "advise" side of the co-pilot).
 * The AI emits an `ai_suggestion` event family discriminated by `suggestion_type`;
 * HITL consumes it and renders it as a one-tap suggestion the human agent can
 * accept or dismiss. The AI never acts on its own once the human has intercepted
 * (the driver rule) — these are advisory only.
 */

export enum SuggestionVerdict {
  // Render the suggestion as an actionable card.
  offer = 'offer',
  // Retract: clears any active suggestion of the same suggestion_type.
  skip = 'skip',
  // HITL-1429: the donation upsell offered after the check readback. A stage
  // marker, not a new kind of card — it renders exactly like `offer`, but it is
  // the only stage whose Accept also adds the upsell item to the agent cart.
  // The AI's mid-order and end-of-order upsell stages stay TTS-only for now
  // (their carrier items are not agent-reachable in the brand menu; MOT-17745).
  postReadbackUpsell = 'post_readback_upsell',
}

/**
 * HITL-1429: which brand-menu upsell the AI is offering, carried on
 * `metadata.upsell`. This is the self-describing half of the payload we
 * classify on — the previous `reason_code` discriminator was dropped from the
 * event twice in seven weeks, so nothing keys off it any more.
 *
 * `constraint_name` is the field that matters: it is per-restaurant and
 * per-charity (`round_up_donation_gigi`, `round_up_donation_childrens_health`,
 * … 20 of them in one Dairy Queen brand menu), so it must never be hardcoded or
 * prefix-matched — it is reverse-looked-up against the menu instead
 * (resolveUpsellSlashItem). `item_name` names the AI's own internal container
 * item (e.g. `post_checkread_back_upsell`), which agents cannot reach, so it is
 * only usable when it happens to be a slash-command item itself.
 */
export interface IUpsellSuggestionDetails {
  item_name?: string;
  constraint_name?: string;
  // The child item selected if the customer says yes / no. Deliberately unused:
  // the TTS is a question the customer has not answered yet, so Accept adds the
  // prompt item and leaves the constraint open (see D5 in the ticket plan).
  accept_value?: string;
  decline_value?: string;
  other_options?: string[];
}

/**
 * The concrete cart mutation the agent applies on Accept. Brand-menu-shaped
 * (item name + constraint), symmetric with the `agent_cart` event, so it maps
 * straight onto a cartSlice mutation.
 */
export interface ISuggestionAction {
  op: 'add_option' | 'add_item' | 'set_quantity' | 'remove';
  target_path: string[];
  constraint_name?: string;
  option?: { item: string; quantity: number };
  quantity?: number;
}

export interface IAISuggestion {
  suggestion_type: string; // discriminator: send_tts, upsell_eligibility, ...
  suggestion_id: string; // correlation for accept/dismiss + the future QA signal
  verdict: SuggestionVerdict | string;
  source?: string; // "AI"
  title: string; // agent-facing label
  reason?: string; // human-readable why
  reason_code?: string; // machine code (mirrors the AI's reason codes)
  action?: ISuggestionAction; // what Accept applies to the cart
  tts?: string; // when present, the line HITL speaks on Accept (any suggestion_type)
  request_id?: string; // echoes the agent_cart request_id this is a response to
  // HITL-1429: brand-menu detail for upsell suggestions. Optional and loosely
  // typed on purpose — every other suggestion_type omits it.
  metadata?: {
    upsell?: IUpsellSuggestionDetails;
    [key: string]: unknown;
  };
}

export interface IAISuggestionTransmissionMessage extends TransmissionMessage {
  data: IAISuggestion;
}

export interface IAISuggestionsState {
  // The single active suggestion, keyed by its suggestion_type. Each newly
  // received suggestion replaces the map wholesale (it reflects the latest cart,
  // so the prior one is stale), so there is only ever one entry. Accept/Dismiss
  // clear the current card; a re-sent suggestion shows again (the AI controls
  // re-emission — if it re-sends, it's still relevant).
  byType: Record<string, IAISuggestion>;
  // request_id of the most recent agent_cart we emitted; used to drop AI
  // suggestions that answer a superseded cart.
  lastRequestId?: string;
}
